import { Logger } from '_common/services';
import { Command } from '../../Command';
import { JsonRange } from 'Editor/services/_Common/Selection';
import { NodeUtils } from 'Editor/services/DataManager';

export class IndentContentCommand extends Command {
  private options: Editor.Edition.StylesCommandOptions;
  private rangeToApply?: Editor.Selection.JsonRange;

  private indentOption: Editor.Edition.IndentOption;

  constructor(
    context: Editor.Edition.Context,
    indentOption: Editor.Edition.IndentOption,
    rangeToApply?: Editor.Selection.JsonRange,
    options: Editor.Edition.StylesCommandOptions = {},
  ) {
    super(context);

    this.indentOption = indentOption;
    this.rangeToApply = rangeToApply;

    this.options = {
      untrackedActionWarning: true,
      createPatch: true,
      ...options,
    };
  }

  private async indentBlock(
    baseModel: Editor.Data.Node.Model,
    blockData: Editor.Data.Node.Data,
    blockPath: Editor.Selection.Path,
  ) {
    if (
      !this.context.DataManager ||
      !this.context.stylesManager ||
      !this.context.contentManipulator
    ) {
      return false;
    }

    if (NodeUtils.isParagraphData(blockData)) {
      const currentIndent =
        this.context.stylesManager.stylesUtils.BlockStylesParser.leftIndentation(blockData, {
          includeInheritedStyles: true,
        }) || 0;
      const newIndent = this.context.stylesManager.stylesUtils.getNextIndentStep(
        currentIndent,
        this.indentOption === 'indent' ? true : false,
      );

      // apply style
      await this.context.contentManipulator.applyStyleToBlock(
        baseModel,
        blockPath,
        'leftIndentation',
        newIndent,
      );

      return true;
    }
  }

  private async indentList(
    baseModel: Editor.Data.Node.Model,
    blockData: Editor.Data.Node.Data,
    blockPath: Editor.Selection.Path,
  ) {
    if (
      !this.context.DataManager ||
      !this.context.stylesManager ||
      !this.context.contentManipulator
    ) {
      return false;
    }

    if (NodeUtils.isParagraphData(blockData) && blockData.id) {
      const listType = this.context.DataManager.numbering.getListType(blockData.id);

      if (listType === 'simple') {
        const listElements: { blockId: string; id: string }[] = [];

        listElements.push({
          blockId: baseModel.id,
          id: blockData.id,
        });

        if (this.indentOption === 'indent') {
          // indent
          await this.context.DataManager.numbering.indentNodes(listElements);
        } else {
          // outdent
          await this.context.DataManager.numbering.outdentNodes(listElements, false);
        }
      } else if (listType === 'outline') {
        const outlineList = this.context.DataManager.numbering.hasBlockADocumentStyleWithList(
          baseModel.id,
          blockData.id,
        );

        if (outlineList) {
          const outlineListStyleId =
            this.context.DataManager.numbering.getStyleIdForList(outlineList);
          const outlineListStyle =
            this.context.DataManager.styles.listStyles.style(outlineListStyleId);

          if (outlineListStyle) {
            let nextStyleLevel;

            if (this.indentOption === 'indent') {
              // indent
              nextStyleLevel = outlineListStyle.getNextLevelForStyleId(blockData.properties.s);
            } else {
              // outdent
              nextStyleLevel = outlineListStyle.getPreviousLevelForStyleId(blockData.properties.s);
            }

            let styleToApply = outlineListStyle.level(nextStyleLevel).et;

            if (styleToApply) {
              // apply style
              // TODO: does this need clear format? if it does call apply styles command
              await this.context.contentManipulator.applyStyleToBlock(
                baseModel,
                blockPath,
                's',
                styleToApply,
              );

              return true;
            } else {
              return await this.indentBlock(baseModel, blockData, blockPath);
            }
          }
        }
      }
    }

    return false;
  }

  private async indendRange(ctx: Editor.Edition.ActionContext) {
    if (
      !this.context.DataManager ||
      !this.context.stylesManager ||
      !this.context.contentManipulator
    ) {
      return false;
    }

    const blockRangesData = JsonRange.splitRangeByTypes(
      this.context.DataManager,
      ctx.range,
      ['p'],
      {
        sameContainerLevel: this.options.sameContainerLevel || false,
        useSelectedCells: this.options.useSelectedCells || true,
      },
    );

    for (let i = 0; i < blockRangesData.length; i++) {
      const range = blockRangesData[i].range;
      const blockData = blockRangesData[i].filteredData;
      const blockPath = blockRangesData[i].filteredDataPath;

      const baseModel = this.context.DataManager.nodes.getNodeModelById(range.start.b);

      // check if element is editable
      if (!baseModel || !this.context.DataManager.nodes.isNodeEditable(baseModel.id)) {
        continue;
      }

      if (this.context.DataManager.numbering.isListElement(baseModel.id)) {
        // indent list
        await this.indentList(baseModel, blockData, blockPath);
      } else {
        // indent block
        await this.indentBlock(baseModel, blockData, blockPath);
      }
    }

    return true;
  }

  protected async handleExec(...args: any[]): Promise<void> {
    if (this.context.debug) {
      Logger.trace('IndentContentCommand exec', this);
    }

    if (!this.context.DataManager || !this.context.stylesManager) {
      return;
    }

    if (this.options.untrackedActionWarning && !this.askUserAboutThis()) {
      return;
    }

    this.buildActionContext(this.rangeToApply);

    if (!this.actionContext) {
      throw new Error('Context is not defined');
    }

    if (await this.indendRange(this.actionContext)) {
      //trigger seelction check
      this.checkSelectionStatus();

      if (this.options.createPatch) {
        this.createPatch();
      }
    }
  }
}
